import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { MaterialModule } from '../../shared/material/material.module';
import { ALERT_TIME_ZONE_OPTIONS } from '../alert-time-zones';
import {
  AlertConfigFormGroup,
  intervalInputMin,
  showDayTargetTime,
  updateTargetTimeValidators,
} from '../alert-config-form.builder';
import { DEFAULT_ALERT_CONFIG, PLATFORM_ALERT_LIMITS, SMS_BODY_MAX } from '../configuration.defaults';
import { SMS_ALLOWED_TOKENS } from '../sms-tokens.util';
import { INTERVAL_UNITS, IntervalUnit } from '../interval-unit.util';
import { NOTIFY_CHANNELS, NotifyChannel } from '../notify-channel.util';

@Component({
  selector: 'app-alert-config-form-fields',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, MatCheckboxModule],
  templateUrl: './alert-config-form-fields.component.html',
  styleUrl: './alert-config-form-fields.component.scss',
})
export class AlertConfigFormFieldsComponent implements OnInit, OnDestroy {
  #dialog = inject(MatDialog);
  #destroy$ = new Subject<void>();

  @Input({ required: true }) form!: AlertConfigFormGroup;
  /** Campaign wizard: show Custom checkbox + five callback rows below retry settings. */
  @Input() showCampaignSchedule = false;
  @Input() cycleSlots?: FormArray;
  @Input() customScheduleControl?: FormControl<boolean>;

  readonly smsMax = SMS_BODY_MAX;
  readonly retryAttemptsMax = PLATFORM_ALERT_LIMITS.maxRetryAttempts;
  readonly minRetryIntervalMinutes = PLATFORM_ALERT_LIMITS.minRetryIntervalMinutes;
  readonly placeholders = DEFAULT_ALERT_CONFIG;
  readonly intervalUnits = INTERVAL_UNITS;
  readonly timeZoneOptions = ALERT_TIME_ZONE_OPTIONS;
  readonly notifyChannels = NOTIFY_CHANNELS;
  readonly smsTokenLabels = SMS_ALLOWED_TOKENS.map((token) => `{${token}}`);
  readonly smsPlaceholderHelp =
    'Placeholder items in {curly brackets} are populated by the system when the alert is sent. Allowed values:';

  ngOnInit(): void {
    this.form.controls.retryIntervalUnit.valueChanges
      .pipe(takeUntil(this.#destroy$))
      .subscribe(() => {
        this.form.controls.retryInterval.updateValueAndValidity();
        updateTargetTimeValidators(this.form);
      });
    updateTargetTimeValidators(this.form);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  smsLength(): number {
    return this.form.controls.smsBody.value.length;
  }

  smsTokenError(): string {
    const unknown = this.form.controls.smsBody.getError('smsTokens')?.unknown as string[] | undefined;
    if (!unknown?.length) {
      return '';
    }
    return `Unknown token: ${unknown.join(', ')}`;
  }

  intervalPlaceholder(): string {
    return this.form.controls.retryIntervalUnit.value === 'minutes'
      ? String(DEFAULT_ALERT_CONFIG.retryIntervalMinutes)
      : '';
  }

  intervalMin(): number {
    return intervalInputMin(this.form);
  }

  showDayTargetTime(): boolean {
    return showDayTargetTime(this.form);
  }

  intervalUnitLabel(unit: IntervalUnit): string {
    if (unit === 'minutes') {
      return 'Minutes';
    }
    if (unit === 'hours') {
      return 'Hours';
    }
    return 'Days';
  }

  notifyChannelLabel(channel: NotifyChannel): string {
    const labels: Record<NotifyChannel, string> = {
      both: 'Text and call',
      textOnly: 'Text only',
      callOnly: 'Call only',
    };
    return labels[channel];
  }

  onUseDefault(): void {
    const current = this.form.controls.smsBody.value.trim();
    if (!current) {
      this.applyDefaultSms();
      return;
    }

    this.#dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Replace message?',
          message: 'Replace the current message with the default template?',
          yesText: 'Replace',
          noText: 'Cancel',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.applyDefaultSms();
        }
      });
  }

  private applyDefaultSms(): void {
    this.form.controls.smsBody.setValue(DEFAULT_ALERT_CONFIG.smsBody);
    this.form.controls.smsBody.markAsDirty();
  }

}
